import React, { useEffect, useState } from "react";
import { Button, Dropdown, Form, Modal, OverlayTrigger, Tab, Table, Tabs, Tooltip } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { AiOutlineCalculator } from "react-icons/ai";
import { BiChevronDown, BiChevronUp, BiGitMerge, BiGridVertical, BiPencil, BiPlus, BiSearch, BiTrash } from "react-icons/bi";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { EquationBuilderFor } from "../../../core/constants/common";
import ESGDataSetService from "../../../core/service/ESGDataSetService";
import GeneralDisclosureService from "../../../core/service/GeneralDisclosureService";
import ConfirmPopup from "../../../core/utilities/ConfirmPopup";
import AddTopic from "../../Common/Standards/AddTopic/AddTopic";
import CreateDataPoint from "../../Common/Standards/CreateDataPoint/CreateDataPoint";
import CreateMetric from "../../Common/Standards/CreateMetric/CreateMetric";
import EquationBuilder from "../../Common/Standards/EquationBuilder/EquationBuilder";
import './../PlatformCodeMapping/PlatformCodeMapping.scss';
import './ESGStandards.scss';
import "./StandardTemplate.scss";

const ESGStandards = ({
	t,
	isConfigMode = false,
	isMappingMode = false,
	mappingStandards,
	standardToMap,
	onAdd,
	onMapMetric,
	onDeleteMetricMapping,
	mappedCodes,
	selectedDataClearedOn,
	onMapDP,
	onDeleteDPMapping,
	onMapGDQ,
	onDeleteGDQMapping,
	onMapQualQuestion,
	onDeleteQualQuestionMapping,
}) => {
	const [activeTab, setActiveTab] = useState('ENVIRONMENTAL');
	const [topics, setTopics] = useState([]);
	const [editedTopic, setEditedTopic] = useState({ id: -1, action: '' });
	const [editedMetric, setEditedMetric] = useState({ id: -1, topicId: 0, action: '' });
	const [editedDP, setEditedDP] = useState({ id: -1, metricId: 0, action: '' });
	const [topicVisiblity, setTopicVisibility] = useState({});
	const [metricVisiblity, setMetricVisibility] = useState({});
	const [pickedData, setPickedData] = useState([]);
	const [editedEQ, setEditedEQ] = useState({ topicId: 0, metricId: 0, action: '' });
	const [selectedMetric, setSelectedMetric] = useState({ id: 0, topicId: 0 });
	const [selectedDP, setSelectedDP] = useState({ id: 0, topicId: 0, metricId: 0, });
	const [questions, setQuestions] = useState({});
	const [questionVisiblity, setQuestionVisibility] = useState({});
	const [selectedGDQ, setSelectedGDQ] = useState({ id: 0, stdType: '' });
	const [selectedQualQues, setSelectedQualQues] = useState({ id: 0, topicId: 0, metricId: 0, childIndex: 0 });
	const [gdStdVisibility, setGDStdVisibility] = useState({});
	const [draggedTopic, setDraggedTopic] = useState(null);
	const [dragOverTopicId, setDragOverTopicId] = useState(null);
	const [draggedMetric, setDraggedMetric] = useState(null);
	const [dragOverMetricId, setDragOverMetricId] = useState(null);
	const [draggedDP, setDraggedDP] = useState(null);
	const [dragOverDPId, setDragOverDPId] = useState(null);
	const [isDragHandleActive, setIsDragHandleActive] = useState(false);

	const canReorder = () => !isConfigMode && !isMappingMode;

	useEffect(() => {
		const resetDragHandle = () => setIsDragHandleActive(false);
		window.addEventListener("mouseup", resetDragHandle);
		return () => window.removeEventListener("mouseup", resetDragHandle);
	}, []);

	const onDragHandleMouseDown = () => {
		setIsDragHandleActive(true);
	};

	const reorderLocally = (list, draggedId, afterId) => {
		const moved = list?.find(x => x.id === draggedId);
		if (!moved) return list;

		const updated = list.filter(x => x.id !== draggedId);
		const afterIndex = updated.findIndex(x => x.id === afterId);
		updated.splice(afterIndex + 1, 0, moved);
		return updated;
	};

	const onTopicDragStart = (topic) => {
		setDraggedTopic(topic);
	};

	const onTopicDragOver = (e, topicId) => {
		e.preventDefault();
		setDragOverTopicId(topicId);
	};

	const onTopicDragEnd = () => {
		setIsDragHandleActive(false);
		if (!draggedTopic || !dragOverTopicId || draggedTopic.id === dragOverTopicId) {
			setDraggedTopic(null);
			setDragOverTopicId(null);
			return;
		}

		setTopics(reorderLocally(topics, draggedTopic.id, dragOverTopicId));
		ESGDataSetService.reorderTopic(draggedTopic.id, { movedToId: dragOverTopicId })
			.finally(() => getTopics(activeTab));
		setDraggedTopic(null);
		setDragOverTopicId(null);
	};

	const isTopicDragTarget = (item) =>
		canReorder() && dragOverTopicId === item.id && draggedTopic?.id !== item.id;

	const isTopicDragging = (item) =>
		canReorder() && draggedTopic?.id === item.id;

	const onMetricDragStart = (metric) => {
		setDraggedMetric(metric);
	};

	const onMetricDragOver = (e, metricId) => {
		e.preventDefault();
		setDragOverMetricId(metricId);
	};

	const onMetricDragEnd = (tIndex) => {
		setIsDragHandleActive(false);
		if (!draggedMetric || !dragOverMetricId || draggedMetric.id === dragOverMetricId) {
			setDraggedMetric(null);
			setDragOverMetricId(null);
			return;
		}

		const topicId = topics[tIndex].id;
		const t = JSON.parse(JSON.stringify(topics));
		t[tIndex].metrics = reorderLocally(t[tIndex].metrics, draggedMetric.id, dragOverMetricId);
		setTopics(t);
		ESGDataSetService.reorderMetric(draggedMetric.id, { movedToId: dragOverMetricId })
			.finally(() => getMetricsForTopic(topicId));
		setDraggedMetric(null);
		setDragOverMetricId(null);
	};

	const isMetricDragTarget = (item) =>
		canReorder() && dragOverMetricId === item.id && draggedMetric?.id !== item.id;

	const isMetricDragging = (item) =>
		canReorder() && draggedMetric?.id === item.id;

	const onDPDragStart = (dp) => {
		setDraggedDP(dp);
	};

	const onDPDragOver = (e, dpId) => {
		e.preventDefault();
		setDragOverDPId(dpId);
	};

	const onDPDragEnd = (tIndex, mIndex) => {
		setIsDragHandleActive(false);
		if (!draggedDP || !dragOverDPId || draggedDP.id === dragOverDPId) {
			setDraggedDP(null);
			setDragOverDPId(null);
			return;
		}

		const topicId = topics[tIndex].id;
		const metricId = topics[tIndex].metrics[mIndex].id;
		const t = JSON.parse(JSON.stringify(topics));
		t[tIndex].metrics[mIndex].dataPoints = reorderLocally(t[tIndex].metrics[mIndex].dataPoints, draggedDP.id, dragOverDPId);
		setTopics(t);
		ESGDataSetService.reorderDP(draggedDP.id, { movedToId: dragOverDPId })
			.finally(() => getDPsForMetric(topicId, metricId));
		setDraggedDP(null);
		setDragOverDPId(null);
	};

	const isDPDragTarget = (item) =>
		canReorder() && dragOverDPId === item.id && draggedDP?.id !== item.id;

	const isDPDragging = (item) =>
		canReorder() && draggedDP?.id === item.id;

	useEffect(() => {
		if (activeTab == 'GD') return;

		getTopics(activeTab);
	}, [activeTab]);

	useEffect(() => {
		if (!selectedMetric.id && !selectedDP.id && !selectedGDQ.id && !selectedQualQues.id) return;

		if (selectedMetric.id > 0) {
			const t = JSON.parse(JSON.stringify(topics));
			t[selectedMetric.tIndex].metrics[selectedMetric.mIndex].standardCodes = mappedCodes;
			setTopics(t);
		} else if (selectedDP.id > 0) {
			const t = JSON.parse(JSON.stringify(topics));
			t[selectedDP.tIndex].metrics[selectedDP.mIndex].dataPoints[selectedDP.dpIndex].standardCodes = mappedCodes;
			setTopics(t);
		} else if (selectedGDQ.id > 0 && selectedGDQ.stdType) {
			const q = JSON.parse(JSON.stringify(questions));
			if (selectedGDQ.childIndex >= 0) {
				q[selectedGDQ.stdType][selectedGDQ.index].children[selectedGDQ.childIndex].standardCodes = mappedCodes;
			} else {
				q[selectedGDQ.stdType][selectedGDQ.index].standardCodes = mappedCodes;
			}
			setQuestions(q);
		} else if (selectedQualQues.id > 0) {
			const t = JSON.parse(JSON.stringify(topics));
			if (selectedQualQues.childIndex >= 0) {
				t[selectedQualQues.tIndex].metrics[selectedQualQues.mIndex].question.children[selectedQualQues.childIndex].standardCodes = mappedCodes;
			} else {
				t[selectedQualQues.tIndex].metrics[selectedQualQues.mIndex].question.standardCodes = mappedCodes;
			}
			setTopics(t);
		}
	}, [mappedCodes]);

	useEffect(() => {
		if (selectedDataClearedOn === 0) return;

		setPickedData([]);
	}, [selectedDataClearedOn]);

	const getTopics = (tab, refreshAll = true, updatedTopicId = 0) => {
		ESGDataSetService.getTopic(0, tab).then(res => {
			if (res.status !== 200) return;

			const newTopics = res.data.data?.sort((a, b) => a.sortOrder - b.sortOrder);
			if (refreshAll) {
				setTopics(newTopics);
			} else {
				const t = JSON.parse(JSON.stringify(topics));
				if (updatedTopicId > 0) {
					const topicIndex = newTopics?.findIndex(newTopic => newTopic.id === updatedTopicId);
					const existingTopicIndex = t?.findIndex(existingTopic => existingTopic.id === updatedTopicId);
					t[existingTopicIndex] = {
						...newTopics[topicIndex],
						metrics: t[existingTopicIndex].metrics ?? []
					};
				} else {
					t.push(newTopics[newTopics.length - 1]);
				}
				setTopics(t);
			}
		}).catch(err => {
		});
	};

	const renderTopicModal = () => {
		if (editedTopic.id === -1 || editedTopic.action === 'DELETE') return <></>;
		return (
			<Modal show={editedTopic.id >= 0} onHide={() => setEditedTopic({ id: -1, action: '' })}
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				<Modal.Header closeButton>
					< Modal.Title as="h5">Add Topic</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<AddTopic
						stdId={0}
						handleDiscard={() => setEditedTopic({ id: -1, action: '' })}
						editTopicId={editedTopic.id}
						handleSave={() => {
							getTopics(activeTab, false, editedTopic.id);
							setEditedTopic({ id: -1, action: '' });
						}}
						currentcategory={activeTab}
						forESGDataSet={true}
					/>
				</Modal.Body>
			</Modal>
		);
	};

	const renderMetricModal = () => {
		if (editedMetric.id === -1 || editedMetric.action === 'DELETE') return <></>;
		return (
			<Modal show={editedMetric.id >= 0} onHide={() => setEditedMetric({ id: -1, topicId: 0 })}
				aria-labelledby="contained-modal-title-vcenter"
				centered
				dialogClassName="modal-75w">
				<Modal.Header closeButton>
					< Modal.Title as="h5">
						{t(editedMetric.id > 0 ? 'EditMetric.label' : 'AddMetric.label')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="1">
					<CreateMetric
						stdId={0}
						handleDiscard={() => setEditedMetric({ id: -1, topicId: 0 })}
						topicId={editedMetric.topicId}
						editMetricId={editedMetric.id}
						answerMode={false}
						handleSave={() => {
							getMetricsForTopic(editedMetric.topicId);
							setEditedMetric({ id: -1, topicId: 0 });
						}}
						forESGDataSet={true}
					/>
				</Modal.Body>
			</Modal>
		);
	};

	const renderDPModal = () => {
		if (editedDP.id === -1 || editedDP.action === 'DELETE') return <></>;
		return (
			<Modal show={editedDP.id >= 0} onHide={() => setEditedDP({ id: -1, metricId: 0 })}
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				<Modal.Header closeButton>
					< Modal.Title as="h5">Add Datapoint</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<CreateDataPoint
						handleDiscard={() => setEditedDP({ id: -1, metricId: 0 })}
						metricId={editedDP.metricId}
						editDataPointId={editedDP.id}
						handleSave={() => {
							getDPsForMetric(editedDP.topicId, editedDP.metricId);
							setEditedDP({ id: -1, metricId: 0 });
						}}
						editData={editedDP.data}
						forESGDataSet={true}
					/>
				</Modal.Body>
			</Modal>
		);
	};

	const toggleTopicVisibility = (id, refreshPickedData = false) => {
		const t = JSON.parse(JSON.stringify(topicVisiblity));
		if (t[id] === true) {
			t[id] = false;
		} else {
			t[id] = true;
			getMetricsForTopic(id, refreshPickedData);
		}
		setTopicVisibility(t);
	};

	const getMetricsForTopic = (id, refreshPickedData = false) => {
		ESGDataSetService.getMetric(0, id, isMappingMode ? standardToMap : '').then(res => {
			if (res.status !== 200) return;

			const metrics = res.data.data?.sort((a, b) => a.sortOrder - b.sortOrder) ?? [];
			const t = JSON.parse(JSON.stringify(topics));
			t?.forEach(topic => {
				if (topic.id !== id) return;

				topic['metrics'] = metrics;
			});
			setTopics(t);
			if (!refreshPickedData) return;

			setPickedData(pData => {
				const topicIndex = pData?.findIndex(t => t.id === id);
				pData[topicIndex].metrics = metrics;
				return pData;
			});
		}).catch(err => { });
	};

	const onTopicSelection = (topic) => {
		const data = JSON.parse(JSON.stringify(pickedData));
		const topicIndex = data?.findIndex(t => t.id === topic.id);
		if (topicIndex >= 0) {
			data.splice(topicIndex, 1);
		} else {
			topic['fromESGDataSet'] = true;
			data.push(topic);
		}
		setPickedData(data);
		if (topic.metricCount > 0 && (!topic.metrics || topic.metrics?.length === 0)) {
			// If the metrics have not been fetched yet, fetch it now
			toggleTopicVisibility(topic.id, true);
		}
	};

	const onMetricSelection = (topic, metric) => {
		const data = JSON.parse(JSON.stringify(pickedData));
		const topicIndex = data?.findIndex(t => t.id === topic.id);
		if (topicIndex < 0) {
			const t = JSON.parse(JSON.stringify(topic));
			t['fromESGDataSet'] = true;
			t.metrics = [];
			t.metrics.push(metric);
			data.push(t);
			setPickedData(data);
			return;
		}
		const metricIndex = data[topicIndex]?.metrics?.findIndex(m => m.id === metric.id);
		if (metricIndex >= 0) {
			data[topicIndex].metrics.splice(metricIndex, 1);
			if (data[topicIndex].metrics?.length === 0) {
				data.splice(topicIndex, 1);
			}
		} else {
			if (data[topicIndex].metrics) {
				data[topicIndex].metrics.push(metric);
			} else {
				data[topicIndex].metrics = [];
				data[topicIndex].metrics.push(metric);
			}
		}
		setPickedData(data);
	};

	const isMetricSelected = (topic, metric) => {
		const topicIndex = pickedData?.findIndex(t => t.id === topic.id);
		const metricIndex = pickedData[topicIndex]?.metrics?.findIndex(m => m.id === metric.id);
		return metricIndex >= 0;
	};

	const renderTopicRows = () => {
		return (
			<>
				{
					topics?.map((topic, tIndex) => {
						return (
							<>
								<tr id={`topicRow-${tIndex}`} key={`topicRow-${tIndex}`}
									className={`topic-row ${isTopicDragTarget(topic) ? ' standards-drop-target' : ''}${isTopicDragging(topic) ? ' opacity-50' : ''}`}
									draggable={canReorder() && isDragHandleActive}
									onDragStart={() => onTopicDragStart(topic)}
									onDragOver={(e) => canReorder() && onTopicDragOver(e, topic.id)}
									onDragEnd={onTopicDragEnd}
								>
									<td>
										{
											isConfigMode &&
											<div className="ES_checkBox">
												<input type="checkbox" className="form-check-input"
													checked={pickedData?.findIndex(t => t.id === topic.id) >= 0}
													onChange={(e) => onTopicSelection(topic)} />
											</div>
										}
										<div className="icon_svg cursor" onClick={() => toggleTopicVisibility(topic.id)}>
											{!topicVisiblity[topic.id] && <BiChevronDown />}
											{topicVisiblity[topic.id] === true && <BiChevronUp />}
										</div>
										<div className="d-flex align-items-center gap-1">
											{
												canReorder() &&
												<span className="topic-drag-handle cursor" onMouseDown={() => onDragHandleMouseDown()}>
													<BiGridVertical />
												</span>
											}
											<h6 className="mb-0">{topic.name}</h6>
										</div>
									</td>
									<td>{topic.code}</td>
									<td></td>
									{
										isConfigMode
											?
											<td></td>
											:
											isMappingMode
												? <td></td>
												:
												<td className="text-end">
													<div className="d-flex gap-1">
														<span className="cursor"
															onClick={() => setEditedTopic({ id: topic.id, action: 'EDIT' })}><BiPencil /></span>
														<span className="cursor"
															onClick={() => setEditedTopic({ id: topic.id, action: 'DELETE' })}><BiTrash /></span>
													</div>
												</td>
									}
								</tr>
								{topicVisiblity[topic.id] === true && renderMetricRows(topic, tIndex)}
							</>
						);
					})
				}
			</>
		);
	};

	const toggleMetricVisibility = (topicId, metricId, cat) => {
		const m = JSON.parse(JSON.stringify(metricVisiblity));
		if (m[metricId] === true) {
			m[metricId] = false;
		} else {
			m[metricId] = true;
			if (cat === 'QUANTITATIVE') {
				getDPsForMetric(topicId, metricId);
			}
		}
		setMetricVisibility(m);
	};

	const getDPsForMetric = (topicId, metricId) => {
		ESGDataSetService.getDP(0, metricId, isMappingMode ? standardToMap : '').then(res => {
			if (res.status !== 200) return;

			const dps = res.data.data?.sort((a, b) => a.sortOrder - b.sortOrder) ?? [];
			const t = JSON.parse(JSON.stringify(topics));
			t?.forEach(topic => {
				if (topic.id !== topicId) return;

				topic.metrics?.forEach(metric => {
					if (metric.id !== metricId) return;

					metric['dataPoints'] = dps;
				});
			});
			setTopics(t);
		}).catch(err => { });
	};

	const categoryToString = (cat) => {
		switch (cat) {
			case 'QUALITATIVE':
				return 'Qualitative';
			case 'QUANTITATIVE':
			default:
				return 'Quantitative';
		}
	};

	const relevantUOMsToString = (uoms) => {
		let str = '';
		uoms?.forEach((u, index) => {
			str += u;

			if (index !== uoms.length - 1) {
				str += ', ';
			}
		})
		return str;
	};

	const renderMetricInfo = (metric) => {
		let childCount = 0;
		if (metric.category === 'QUANTITATIVE') {
			childCount = metric.dataPointCount;
		} else {
			if (metric.question) {
				childCount++;
			}
			if (metric.question?.children) {
				childCount += metric.question?.children?.length;
			}
		}
		return (
			<div>
				<ul className="Measuring_Uom_view">
					<li>{categoryToString(metric.category)}</li>
					{
						(metric.unitOfMeasure || metric.relevantUoms?.length > 0) &&
						<li>
							<span className="fw-600">{metric.unitOfMeasure}</span>
							{
								metric.relevantUoms?.length > 0 && <span>{relevantUOMsToString(metric.relevantUoms)}</span>
							}
						</li>
					}
					<li>{childCount} <BiGitMerge /></li>
				</ul>
			</div>
		);
	};

	const renderMetricRows = (topic, tIndex) => {
		return (
			<>
				{
					topic?.metrics?.map((metric, mIndex) => {
						return (
							<>
								<tr id={`metricRow-${tIndex}-${mIndex}`} key={`metricRow-${tIndex}-${mIndex}`}
									className={`metric-row ${selectedMetric.id === metric.id ? 'Select_row' : ''} ${isMetricDragTarget(metric) ? ' standards-drop-target' : ''}${isMetricDragging(metric) ? ' opacity-50' : ''}`}
									draggable={canReorder() && isDragHandleActive}
									onDragStart={() => onMetricDragStart(metric)}
									onDragOver={(e) => canReorder() && onMetricDragOver(e, metric.id)}
									onDragEnd={() => onMetricDragEnd(tIndex)}
								>
									<td>
										{
											isConfigMode &&
											<div className="ES_checkBox">
												<input type="checkbox" className="form-check-input"
													checked={isMetricSelected(topic, metric)}
													onChange={(e) => onMetricSelection(topic, metric)} />
											</div>
										}
										<div className="icon_svg cursor" onClick={() => toggleMetricVisibility(topic.id, metric.id, metric.category)}>
											{!metricVisiblity[metric.id] && <BiChevronDown />}
											{metricVisiblity[metric.id] === true && <BiChevronUp />}
										</div>
										<div className="metric-row_in">
											<div className="d-flex align-items-center gap-1">
												{
													canReorder() &&
													<span className="metric-drag-handle cursor" onMouseDown={() => onDragHandleMouseDown()}>
														<BiGridVertical />
													</span>
												}
												<div>
													<h6>{metric.name}</h6>
													{(isConfigMode || isMappingMode) && renderMetricInfo(metric)}
												</div>
											</div>
											{!isConfigMode && !isMappingMode && renderMetricInfo(metric)}
										</div>
									</td>
									<td>{metric.code}</td>
									{
										isConfigMode
											?
											<td>
												{
													metric.equation?.expression &&
													<OverlayTrigger placement="top"
														overlay={<Tooltip id="eqTooltip">{metric.equation?.expression ?? ''}</Tooltip>}>
														<i className="cursor">fx</i>
													</OverlayTrigger>
												}
											</td>
											:
											isMappingMode
												?
												<td>{metric.standardCodes?.toString()}</td>
												:
												<td>{renderEquationBuilder(tIndex, topic, mIndex, metric)}</td>
									}
									{
										isConfigMode
											?
											<td></td>
											:
											<td className="text-end">
												<div className="d-flex gap-1">
													<span className="cursor"
														onClick={() => onEditMetric(topic, tIndex, metric, mIndex)}><BiPencil /></span>
													<span className="cursor"
														onClick={() => onDeleteMetric(topic, tIndex, metric, mIndex)}><BiTrash /></span>
												</div>
											</td>
									}
								</tr>
								{metricVisiblity[metric.id] === true && metric.category === 'QUANTITATIVE' && renderDPRows(topic, metric, tIndex, mIndex)}
								{metricVisiblity[metric.id] === true && metric.category === 'QUALITATIVE' && renderQualMetricQuestionRows(topic, metric, tIndex, mIndex)}
							</>
						);
					})
				}
				{
					!isConfigMode && !isMappingMode &&
					<tr className="metric-row">
						<td colSpan={4}>
							<span className="cursor text-primary fw-600"
								onClick={() => setEditedMetric({ id: 0, topicId: topic.id, action: 'ADD' })}><BiPlus /> Metric</span>
						</td>
					</tr>
				}
			</>
		);
	};

	const onEditMetric = (topic, tIndex, metric, mIndex) => {
		if (isMappingMode) {
			onMapMetric(metric);
			setSelectedMetric({ id: metric.id, mIndex: mIndex, tIndex: tIndex, topicId: topic.id });
			setSelectedDP({ id: 0, topicId: 0, metricId: 0 });
			setSelectedGDQ({ id: 0 });
			setSelectedQualQues({ id: 0, topicId: 0, metricId: 0, childIndex: 0 });
		} else {
			setEditedMetric({ id: metric.id, topicId: topic.id, action: 'EDIT' });
		}
	};

	const onDeleteMetric = (topic, tIndex, metric, mIndex) => {
		if (isMappingMode) {
			ESGDataSetService.deletePlatformCodeMapping(standardToMap, metric.code).then(res => {
				if (res.status !== 200 && res.status !== 204) return;

				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				const t = JSON.parse(JSON.stringify(topics));
				t[selectedMetric.tIndex].metrics[selectedMetric.mIndex].standardCodes = [];
				setTopics(t);
				onDeleteMetricMapping('');
			})
		} else {
			setEditedMetric({ id: metric.id, topicId: topic.id, action: 'DELETE' })
		}
	};

	const renderEquationBuilder = (tIndex, topic, mIndex, metric) => {
		return (
			<div className="AddEquation_box equation-dropdown-cell 8">
				{metric.category === 'QUANTITATIVE' && (
					<Dropdown
						show={editedEQ.metricId === metric.id}
						onToggle={(nextShow) => {
							if (nextShow) {
								setEditedEQ({
									topicId: topic.id,
									metricId: metric.id,
									tIndex,
									mIndex,
									equation: metric.equation
								});
							} else {
								setEditedEQ({ metricId: 0, topicId: 0 });
							}
						}}
						autoClose={false}
					>
						<Dropdown.Toggle
							variant="ShowEquation"
							id={`eqBuilder-${tIndex}-${mIndex}`}
							className="equation-dropdown-toggle"
						>
							{metric.equation ? (
								<span className="equation-expression-inner">
									<AiOutlineCalculator className="equation-expression-icon flex-shrink-0" aria-hidden />
									<span className="equation-expression-text">{metric.equation.expression}</span>
								</span>
							) : (
								<>
									{metric.category === 'QUANTITATIVE' && (
										<>
											<BiPlus /> {t('Equation.label')}
										</>
									)}
								</>
							)}
						</Dropdown.Toggle>
						<Dropdown.Menu
							className="p-0 equation-dropdown-menu-esg"
							popperConfig={{ strategy: 'fixed' }}
						>
							{editedEQ.metricId === metric.id && (
								<EquationBuilder
									stdId={0}
									metricId={editedEQ.metricId}
									topicId={editedEQ.topicId}
									discardBtnClick={() => setEditedEQ({ metricId: 0 })}
									equationObj={editedEQ.equation}
									updatedEquationObj={(eq) => {
										const next = JSON.parse(JSON.stringify(topics));
										next[editedEQ.tIndex].metrics[editedEQ.mIndex].equation = eq;
										setTopics(next);
										setEditedEQ({ metricId: 0 });
									}}
									buildFor={EquationBuilderFor.ESG_DATASET}
								/>
							)}
						</Dropdown.Menu>
					</Dropdown>
				)}
			</div>
		);
	};

	const renderDPRows = (topic, metric, tIndex, mIndex) => {
		return (
			<>
				{
					metric?.dataPoints?.map((dp, dpIndex) => {
						return (
							<tr id={`dpRow-${tIndex}-${mIndex}-${dpIndex}`} key={`dpRow-${tIndex}-${mIndex}-${dpIndex}`}
								className={`DP-row ${selectedDP.id === dp.id ? 'Select_row' : ''} ${isDPDragTarget(dp) ? ' standards-drop-target' : ''}${isDPDragging(dp) ? ' opacity-50' : ''}`}
								draggable={canReorder() && isDragHandleActive}
								onDragStart={() => onDPDragStart(dp)}
								onDragOver={(e) => canReorder() && onDPDragOver(e, dp.id)}
								onDragEnd={() => onDPDragEnd(tIndex, mIndex)}
							>
								<td>
									<div className="DP-row_in">
										<div className="d-flex align-items-center gap-1">
											{
												canReorder() &&
												<span className="dp-drag-handle cursor" onMouseDown={() => onDragHandleMouseDown()}>
													<BiGridVertical />
												</span>
											}
											<div>{dp.name}</div>
										</div>
										<div>
											<ul className="Measuring_Uom_view">
												<li><b>{dp.unitOfMeasure}</b></li>
											</ul>
										</div>
									</div>
								</td>
								<td>{dp.code}</td>
								{
									isMappingMode
										?
										<td>{dp.standardCodes?.toString()}</td>
										:
										<td></td>
								}
								{
									isConfigMode
										? <td></td>
										:
										<td className="text-end">
											<div className="d-flex gap-1">
												<span className="p-1 cursor"
													onClick={() => onEditDP(topic, tIndex, metric, mIndex, dp, dpIndex)}><BiPencil /></span>
												<span className="p-1 cursor"
													onClick={() => onDeleteDP(topic, tIndex, metric, mIndex, dp, dpIndex)}><BiTrash /></span>
											</div>
										</td>
								}
							</tr>
						);
					})
				}
				{
					!isConfigMode && !isMappingMode &&
					<tr className="DP-row">
						<td colSpan={5}>
							<div className="DP-row_in">
								{
									metric.category === 'QUANTITATIVE' ? (
										<span className="cursor text-primary fw-600"
											onClick={() => setEditedDP({
												id: 0,
												metricId: metric.id,
												topicId: topic.id,
												action: 'ADD'
											})}><BiPlus /> {t('DataPoint.label')}</span>
									) : metric.category === 'QUALITATIVE' ? (
										<span className="cursor text-primary fw-600"
											onClick={() => setEditedMetric({ id: metric.id, topicId: topic.id, action: 'EDIT' })}><BiPlus /> Question</span>
									) : null
								}
							</div>
						</td>
					</tr>
				}
			</>
		);
	};

	const onEditDP = (topic, tIndex, metric, mIndex, dp, dpIndex) => {
		if (isMappingMode) {
			onMapDP(dp);
			setSelectedDP({
				id: dp.id,
				metricId: metric.id,
				mIndex: mIndex,
				topicId: topic.id,
				tIndex: tIndex,
				dpIndex: dpIndex
			});
			setSelectedMetric({ id: 0, topicId: 0 });
			setSelectedGDQ({ id: 0 });
			setSelectedQualQues({ id: 0, topicId: 0, metricId: 0, childIndex: 0 });
		} else {
			setEditedDP({
				id: dp.id,
				metricId: metric.id,
				topicId: topic.id,
				action: 'EDIT',
				data: dp
			});
		}
	};

	const onDeleteDP = (topic, tIndex, metric, mIndex, dp, dpIndex) => {
		if (isMappingMode) {
			ESGDataSetService.deletePlatformCodeMapping(standardToMap, dp.code).then(res => {
				if (res.status !== 200 && res.status !== 204) return;

				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				const t = JSON.parse(JSON.stringify(topics));
				t[selectedDP.tIndex].metrics[selectedDP.mIndex].dataPoints[selectedDP.dpIndex].standardCodes = [];
				setTopics(t);
				onDeleteDPMapping('');
			})
		} else {
			setEditedDP({
				id: dp.id,
				metricId: metric.id,
				topicId: topic.id,
				action: 'DELETE'
			});
		}
	};

	const renderTabContent = () => {
		return (
			<div className={isConfigMode ? 'ECodrisilStandard_View_Card' : "EcodrisilStandards_SetCard"}>
				<Table>
					{
						isMappingMode &&
						<thead>
							<tr>
								<th>Ecodrisil</th>
								<th></th>
								<th style={{ width: "30%" }}>{standardToMap}</th>
								<th></th>
							</tr>
						</thead>
					}
					<tbody>
						{renderTopicRows()}
					</tbody>
				</Table>
				{
					!isConfigMode &&
					<div className="EcodrisilStandards_SetCard_Footer">
						{
							isMappingMode
								?
								<div>
									<Button variant="secondary" size="sm" className="me-2 px-3">{t("Cancel.label")}</Button>
									<Button variant="primary" size="sm" className="px-3"
										onClick={() => {
											if (isMappingMode) {
												onSaveMapping();
											} else {

											}
										}}>{t("Save.label")}</Button>
								</div>
								:
								<div>
									<Button variant="secondary" size="sm"
										onClick={() => setEditedTopic({ id: 0, action: 'ADD' })}><BiPlus className="me-1" /> Topic</Button>
								</div>
						}
					</div>
				}
			</div>
		);
	};

	const onTabchange = (k) => {
		setTopicVisibility({});
		setMetricVisibility({});
		setActiveTab(k);
	};

	const renderDeleteConfirmationModal = () => {
		if (editedTopic.action !== 'DELETE'
			&& editedMetric.action !== 'DELETE'
			&& editedDP.action !== 'DELETE') return;

		return (
			<ConfirmPopup
				title={t("confirmation.label")}
				open={editedTopic.id > 0 || editedMetric.id > 0 || editedDP.id > 0}
				setOpen={() => {
					setEditedTopic({ id: -1 });
					setEditedMetric({ id: -1, topicId: 0 });
					setEditedDP({ id: -1, metricId: 0 });
				}}
				onConfirm={() => deleteEntity()}>
				<div className='text-center'>{t("ConfirmDelete.label")}</div>
			</ConfirmPopup>
		);

	};

	const deleteEntity = () => {
		let req;
		if (editedTopic.id > 0) {
			req = ESGDataSetService.deleteTopic(editedTopic.id);
		} else if (editedMetric.id > 0) {
			req = ESGDataSetService.deleteMetric(editedMetric.id);
		} else if (editedDP.id > 0) {
			req = ESGDataSetService.deleteDP(editedDP.id);
		}
		req?.then(res => {
			if (res.status !== 200) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			removeTopicMetricOrDPFromStatePostDeletion();
			setEditedTopic({ id: -1 });
			setEditedMetric({ id: -1, topicId: 0 });
			setEditedDP({ id: -1, metricId: 0 });
		})
			.catch((error) => { });
	};

	const removeTopicMetricOrDPFromStatePostDeletion = () => {
		const t = JSON.parse(JSON.stringify(topics));
		if (editedTopic.id > 0) {
			const tIndex = t.findIndex(t => t.id === editedTopic.id);
			if (tIndex < 0) return;

			t.splice(tIndex, 1);
		} else if (editedMetric.id > 0) {
			const tIndex = t.findIndex(t => t.id === editedMetric.topicId);
			if (tIndex < 0) return;

			const mIndex = t[tIndex].metrics?.findIndex(m => m.id === editedMetric.id);
			if (mIndex < 0) return;

			t[tIndex].metrics.splice(mIndex, 1);
		} else if (editedDP.id > 0) {
			const tIndex = t.findIndex(t => t.id === editedDP.topicId);
			if (tIndex < 0) return;

			const mIndex = t[tIndex].metrics?.findIndex(m => m.id === editedDP.metricId);
			if (mIndex < 0) return;

			const dpIndex = t[tIndex].metrics[mIndex]?.dataPoints?.findIndex(d => d.id === editedDP.id);
			if (dpIndex < 0) return;

			t[tIndex].metrics[mIndex].dataPoints?.splice(dpIndex, 1);
		}
		setTopics(t);
	};

	const renderTabs = () => {
		return (
			<Tabs
				id="controlled-tab-example"
				activeKey={activeTab}
				onSelect={(k) => onTabchange(k)}
				className="EcodrisilStandards_tabs">
				<Tab eventKey="ENVIRONMENTAL" title="Enivronmental">
					{renderTabContent()}
				</Tab>
				<Tab eventKey="SOCIAL" title="Social">
					{renderTabContent()}
				</Tab>
				<Tab eventKey="GOVERNANCE" title="Governance" >
					{renderTabContent()}
				</Tab>
				{
					isMappingMode &&
					<Tab eventKey="GD" title="General Disclosures" >
						{renderGDTabContent()}
					</Tab>
				}
			</Tabs>
		)
	};

	const getContainerClass = () => {
		if (isConfigMode) {
			return 'card Ecodrisil_Standards_card';
		} else if (isMappingMode) {
			return 'card';
		} else {
			return 'EcodrisilStandards';
		}
	};

	const renderHeader = () => {
		if (isConfigMode) {
			return (
				<div className="Ecodrisil_Standards_card_head">
					{t("ESGDataset.label")}
					<Button variant="primary" size="sm" className="me-2 px-3" onClick={() => onAdd(pickedData)}>{t('Add.label')}</Button>
				</div>
			);
		} else if (isMappingMode) {
			return (
				<div className="ES_SearchBox">
					<div className="SearchBox">
						<Form>
							<input
								type="text"
								className="form-control"

							/>
							<Link to="#" className="Search_iocnBox">
								<BiSearch />
							</Link>
						</Form>
					</div>
				</div>
			);
		} else {
			return (
				<div className="List_header">
					<h5 className="MH">{t("ESGDataset.label")}</h5>
				</div>
			);
		}
	};

	const onSaveMapping = () => {
		const payload = { mappings: [] };
		topics?.forEach(t => {
			t.metrics?.forEach(m => {
				const metricCodes = [];
				m.standardCodes?.forEach(code => {
					if (m.category === 'QUANTITATIVE') {
						metricCodes.push({
							standardCode: code,
							domain: 'QUANTITATIVE_METRIC'
						});
					} else {
						metricCodes.push({
							standardCode: code,
							domain: 'QUALITATIVE_METRIC'
						});
					}
				});
				payload.mappings.push({
					standard: standardToMap,
					platformCode: m.code,
					standardCodes: metricCodes
				});
				m.dataPoints?.forEach(dp => {
					const dpCodes = [];
					dp.standardCodes?.forEach(code => {
						dpCodes.push({
							standardCode: code,
							domain: 'METRIC_DATA_POINT'
						});
					});
					// DP mapping
					payload.mappings.push({
						standard: standardToMap,
						platformCode: dp.code,
						standardCodes: dpCodes
					});
				});
				// Material topic mapping
				const topicCodes = [];
				m.question?.standardCodes?.forEach(code => {
					topicCodes.push({
						standardCode: code,
						domain: 'QUALITATIVE_METRIC'
					});
				});
				if (topicCodes?.length > 0) {
					payload.mappings.push({
						standard: standardToMap,
						platformCode: m.question.code,
						standardCodes: topicCodes
					});
				}
				m.question?.children?.forEach(child => {
					const topicCodes = [];
					child?.standardCodes?.forEach(code => {
						topicCodes.push({
							standardCode: code,
							domain: 'QUALITATIVE_METRIC'
						});
					});
					if (topicCodes?.length > 0) {
						payload.mappings.push({
							standard: standardToMap,
							platformCode: child.code,
							standardCodes: topicCodes
						});
					}
				});
			});
		});
		ESGDataSetService.savePlatformCodeMapping(payload).then(res => {
			if (res.status !== 200) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}).catch(err => { });
	};

	const renderGDTabContent = () => {
		return (
			<div className="EcodrisilStandards_SetCard">
				<Table>
					<thead>
						<tr>
							<th>Ecodrisil</th>
							<th></th>
							<th style={{ width: "30%" }}>Code</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{renderGDStandards()}
					</tbody>
				</Table>
				<div className="EcodrisilStandards_SetCard_Footer">
					<div>
						<Button variant="secondary" size="sm" className="me-2 px-3">{t("Cancel.label")}</Button>
						<Button variant="primary" size="sm" className="px-3"
							onClick={() => onSaveGDMapping()}>{t("Save.label")}</Button>
					</div>
				</div>
			</div>
		);
	};

	const renderGDStandards = () => {
		return (
			<>
				{
					mappingStandards?.map((stdType, stdIndex) => {
						return (
							<>
								<tr id={`gdStdRow-${stdIndex}`} key={`gdStdRow-${stdIndex}`}
									className={`topic-row`}>
									<td colSpan={5}>
										<div className="icon_svg cursor" onClick={() => toggleGDStdVisibility(stdType)}>
											{!gdStdVisibility[stdType] && <BiChevronDown />}
											{gdStdVisibility[stdType] === true && <BiChevronUp />}
										</div>
										<h6>{stdType}</h6>
									</td>
								</tr>
								{gdStdVisibility[stdType] === true && renderGDQuestions(stdType)}
							</>
						);
					})
				}
			</>
		);
	};

	const renderGDQuestions = (stdType) => {
		return (
			<>
				{
					questions[stdType]?.map((question, qIndex) => {
						return (
							<>
								<tr id={`questionRow-${qIndex}`} key={`questionRow-${qIndex}`}
									className={`metric-row ${selectedGDQ.id === question.id ? 'Select_row' : ''}`}>
									<td>
										<div className="icon_svg cursor" onClick={() => toggleQuestionVisibility(question.id)}>
											{!questionVisiblity[question.id] && <BiChevronDown />}
											{questionVisiblity[question.id] === true && <BiChevronUp />}
										</div>
										<h6>{question.name}</h6>
									</td>
									<td>{question.code}</td>
									<td></td>
									<td>{question.standardCodes?.toString()}</td>
									<td className="text-end">
										<div className="d-flex gap-1">
											<span className="p-1 cursor"
												onClick={() => onEditGDQ(stdType, question, qIndex)}><BiPencil /></span>
											<span className="p-1 cursor"
												onClick={() => onDeleteGDQ(stdType, question, qIndex)}><BiTrash /></span>
										</div>
									</td>
								</tr>
								{questionVisiblity[question.id] === true && renderGDQChildrenRows(stdType, question, qIndex)}
							</>
						);
					})
				}
			</>
		);
	};

	const renderGDQChildrenRows = (stdType, question, qIndex) => {
		return (
			<>
				{
					question?.children?.map((child, cIndex) => {
						return (
							<>
								<tr id={`questionRow-${qIndex}-${cIndex}`} key={`questionRow-${qIndex}-${cIndex}`}
									className={`DP-row ${selectedGDQ.id === child.id ? 'Select_row' : ''}`}>
									<td>
										<h6>{child.name}</h6>
									</td>
									<td>{child.code}</td>
									<td></td>
									<td>{child.standardCodes?.toString()}</td>
									<td className="text-end">
										<div className="d-flex gap-1">
											<span className="p-1 cursor"
												onClick={() => onEditGDQ(stdType, child, qIndex, cIndex)}><BiPencil /></span>
											<span className="p-1 cursor"
												onClick={() => onDeleteGDQ(stdType, child, qIndex, cIndex)}><BiTrash /></span>
										</div>
									</td>
								</tr>
							</>
						);
					})
				}
			</>
		);
	};


	const toggleGDStdVisibility = (stdType) => {
		const t = JSON.parse(JSON.stringify(gdStdVisibility));
		if (t[stdType] === true) {
			t[stdType] = false;
		} else {
			t[stdType] = true;
		}
		setGDStdVisibility(t);
		if (!t[stdType]) return;

		getGDQuestions(stdType);
	};

	const toggleQuestionVisibility = (id) => {
		const t = JSON.parse(JSON.stringify(questionVisiblity));
		if (t[id] === true) {
			t[id] = false;
		} else {
			t[id] = true;
		}
		setQuestionVisibility(t);
	};


	const onEditGDQ = (stdType, question, qIndex, childIndex = -1) => {
		onMapGDQ(question);
		setSelectedGDQ({ id: question.id, stdType: stdType, index: qIndex, childIndex: childIndex });
		setSelectedDP({ id: 0, topicId: 0, metricId: 0 });
		setSelectedMetric({ id: 0, topicId: 0 });
		setSelectedQualQues({ id: 0, topicId: 0, metricId: 0, childIndex: 0 });
	};

	const onDeleteGDQ = (stdType, question, qIndex, childIndex = -1) => {
		ESGDataSetService.deletePlatformCodeMapping(standardToMap, question.code).then(res => {
			if (res.status !== 200 && res.status !== 204) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			const q = JSON.parse(JSON.stringify(questions));
			if (childIndex >= 0) {
				q[stdType][qIndex].children[childIndex].standardCodes = [];
			} else {
				q[stdType][qIndex].standardCodes = [];
			}
			setQuestions(q);
			onDeleteGDQMapping('');
		})
	};

	const getGDQuestions = (stdType) => {
		GeneralDisclosureService.getAllQuestionsForStdType(stdType).then(res => {
			const q = JSON.parse(JSON.stringify(questions));
			q[stdType] = res.data?.data ?? [];
			setQuestions(q);
		}).catch(err => { });
	};

	const onSaveGDMapping = () => {
		const payload = { mappings: [] };
		mappingStandards?.forEach(std => {
			questions[std]?.forEach(q => {
				const gdCodes = [];
				q.standardCodes?.forEach(code => {
					gdCodes.push({
						standardCode: code,
						domain: 'GENERAL_DISCLOSURE'
					});
				});
				payload.mappings.push({
					standard: standardToMap,
					platformCode: q.code,
					standardCodes: gdCodes
				});
				q.children?.forEach(child => {
					const childCodes = [];
					child.standardCodes?.forEach(code => {
						childCodes.push({
							standardCode: code,
							domain: 'GENERAL_DISCLOSURE'
						});
					});
					payload.mappings.push({
						standard: standardToMap,
						platformCode: child.code,
						standardCodes: childCodes
					});
				});
			});
		});

		ESGDataSetService.savePlatformCodeMapping(payload).then(res => {
			if (res.status !== 200) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}).catch(err => { });
	};

	const renderQualMetricQuestionRows = (topic, metric, tIndex, mIndex) => {
		return (
			<>
				<tr id={`quesRow-${tIndex}-${mIndex}`} key={`quesRow-${tIndex}-${mIndex}`}
					className={`DP-row ${selectedQualQues.id === metric.question.id ? 'Select_row' : ''}`}>
					<td>
						<div className="DP-row_in">
							<div>{metric.question.question}</div>
						</div>
					</td>
					<td>{metric.question.code}</td>
					{
						isMappingMode
							?
							<td>{metric.question.standardCodes?.toString()}</td>
							:
							<td></td>
					}
					{
						isConfigMode
							? <td></td>
							:
							<td className="text-end">
								<div className="d-flex gap-1">
									<span className="p-1 cursor"
										onClick={() => onEditQualitativeQuestion(topic, tIndex, metric, mIndex, metric.question, -1)}><BiPencil /></span>
									<span className="p-1 cursor"
										onClick={() => onDeleteQualitativeQuestion(metric.question.code)}><BiTrash /></span>
								</div>
							</td>
					}
				</tr>
				{
					metric.question?.children?.map((child, cIndex) => {
						return (
							<tr id={`quesRow-${tIndex}-${mIndex}-${cIndex}`} key={`quesRow-${tIndex}-${mIndex}-${cIndex}`}
								className={`DP-row ${selectedQualQues.id === child.id ? 'Select_row' : ''}`}>
								<td>
									<div className="DP-row_in">
										<div>{child.question}</div>
									</div>
								</td>
								<td>{child.code}</td>
								{
									isMappingMode
										?
										<td>{child.standardCodes?.toString()}</td>
										:
										<td></td>
								}
								{
									isConfigMode
										? <td></td>
										:
										<td className="text-end">
											<div className="d-flex gap-1">
												<span className="p-1 cursor"
													onClick={() => onEditQualitativeQuestion(topic, tIndex, metric, mIndex, child, cIndex)}><BiPencil /></span>
												<span className="p-1 cursor"
													onClick={() => onDeleteQualitativeQuestion(child.code)}><BiTrash /></span>
											</div>
										</td>
								}
							</tr>
						);
					})
				}
				{
					!isConfigMode && !isMappingMode &&
					<tr className="DP-row">
						<td colSpan={5}>
							<div className="DP-row_in">
								<span className="cursor text-primary fw-600"
									onClick={() => setEditedMetric({ id: metric.id, topicId: topic.id, action: 'EDIT' })}><BiPlus /> Question</span>
							</div>
						</td>
					</tr>
				}
			</>
		);
	};

	const onEditQualitativeQuestion = (topic, tIndex, metric, mIndex, question, qIndex) => {
		if (!isMappingMode) return;

		onMapQualQuestion(question);
		setSelectedQualQues({
			id: question.id,
			topicId: topic.id,
			tIndex: tIndex,
			metricId: metric.id,
			mIndex: mIndex,
			childIndex: qIndex >= 0 ? qIndex : -1
		});
		setSelectedMetric({ id: 0, topicId: 0 });
		setSelectedDP({ id: 0, topicId: 0, metricId: 0 });
		setSelectedGDQ({ id: 0 });
	};

	const onDeleteQualitativeQuestion = (code) => {
		if (!isMappingMode) return;

		ESGDataSetService.deletePlatformCodeMapping(standardToMap, code).then(res => {
			if (res.status !== 200 && res.status !== 204) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			const t = JSON.parse(JSON.stringify(topics));
			if (selectedQualQues.childIndex === 0) {
				t[selectedQualQues.tIndex].metrics[selectedQualQues.mIndex].question.standardCodes = [];
			} else {
				t[selectedQualQues.tIndex].metrics[selectedQualQues.mIndex].question.children[selectedQualQues.childIndex].standardCodes = [];
			}
			setTopics(t);
			onDeleteQualQuestionMapping('');
		})
	};

	return (
		<div className={getContainerClass()}>
			{renderHeader()}
			{
				isConfigMode
					?
					<div className="Ecodrisil_Standards_card_body">
						{renderTabs()}
					</div>
					:
					isMappingMode
						?
						renderTabs()
						:
						<div className="row m-0">
							<div className="EcodrisilStandards_card col-lg-12 p-2">
								<div className="card">
									<Button variant="primary" size="sm" className="Btn_Codemapping">Go to code mapping</Button>
									{renderTabs()}
								</div>
							</div>
							{renderTopicModal()}
							{renderMetricModal()}
							{renderDPModal()}
							{renderDeleteConfirmationModal()}
						</div>
			}
		</div>
	);

};
export default withTranslation()(ESGStandards);
